// ============================================================================
// Shared User Preference Helpers - thin wrapper around localStorage that
// handles JSON parsing and swallows storage errors (quota, disabled storage,
// cross-origin iframe sandboxing). For ephemeral UI state only.
//
// For domain data with TTL + cache invalidation use PersistentStore instead.
// ============================================================================

/**
 * Read a preference value. Returns the parsed JSON value, or `fallback`
 * if the key is missing or the stored value is unparseable.
 *
 * @template T
 * @param {string} key
 * @param {T} [fallback=null]
 * @returns {T}
 */
export function getPref(key, fallback = null) {
    try {
        const raw = localStorage.getItem(key);
        if (raw === null) return fallback;
        return JSON.parse(raw);
    } catch {
        return fallback;
    }
}

/**
 * Write a preference value. Values are JSON-stringified so strings, numbers,
 * booleans, arrays, and plain objects all round-trip cleanly through getPref.
 * Silent on quota / permission errors.
 *
 * @param {string} key
 * @param {unknown} value
 */
export function setPref(key, value) {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
        console.warn(`[userPrefs] setPref(${key}) failed:`, err.message);
    }
}

/**
 * Remove a preference. Silent on error.
 * @param {string} key
 */
export function removePref(key) {
    try {
        localStorage.removeItem(key);
    } catch { /* ignore */ }
}
